import { createTheme } from "@shopify/restyle";
import { Platform } from "react-native";

import {
  whooshButtonColors,
  whooshIconColors,
  whooshPrimitiveTokens,
  whooshSurfaceColors,
  whooshTextColors,
  whooshInputColors,
} from "../colors";
import { whooshOutlineColors } from "../colors/outline";
import { whooshUserSelectableColors } from "../colors/userSelectable";

export type WhooshThemeColors = keyof Theme["colors"];

export const theme = createTheme({
  colors: {
    ...whooshPrimitiveTokens,
    ...whooshTextColors,
    ...whooshSurfaceColors,
    ...whooshIconColors,
    ...whooshButtonColors,
    ...whooshOutlineColors,
    ...whooshUserSelectableColors,
    ...whooshInputColors,
  },
  breakpoints: {
    base: 0,
    xs: 368,
    375: 375,
    sm: 480,
    544: 544,
    md: 768,
    800: 800,
    agendaSplit: 834,
    POSMd: 900,
    eventCardColumn: 990,
    995: 995,
    lg: 1100,
    POSLg: 1200,
    xl: 1280,
    xxl: 1650,
  },
  borderRadii: {
    "0": 0,
    "0.5": 2,
    "1": 4,
    "2": 8,
    "4": 16,
    "6": 24,
    "8": 32,
    full: 9999,
  },
  zIndices: {
    [-1]: -1,
    back: 0,
    1: 1,
    10: 10,
    100: 100,
    200: 200,
    300: 300,
    400: 400,
    500: 500,
    600: 600,
    700: 700,
    800: 800,
    900: 900,
    1000: 1000,
    10000: 10000,
  },
  spacing: {
    "-10": -10,
    "-6": -24,
    "-1": -1,
    0.5: 2,
    ".5": 2,
    0: 0,
    1: 4,
    "1.5": 6,
    2: 8,
    "2.5": 10,
    3: 12,
    4: 16,
    5: 20,
    6: 24,
    7: 28,
    8: 32,
    9: 36,
    10: 40,
    11: 44,
    12: 48,
    13: 52,
    14: 56,
    15: 60,
    16: 64,
    17: 68,
    18: 72,
    19: 76,
    20: 80,
    21: 84,
    22: 88,
    23: 92,
    24: 96,
    25: 100,
    29: 116,
    32: 128,
    34: 136,
    auto: "auto",
  },
  textVariants: {
    mainTitle2: {
      fontWeight: "normal",
      fontFamily:
        Platform.OS === "web" ? "Inter_500Medium, Inter" : "Inter_500Medium",
      fontSize: 28,
      lineHeight: 32,
      letterSpacing: 28 * -0.02,
    },
    mainTitle3: {
      fontWeight: "normal",
      fontFamily:
        Platform.OS === "web" ? "Inter_500Medium, Inter" : "Inter_500Medium",
      fontSize: 24,
      lineHeight: 32,
      letterSpacing: 24 * -0.03,
    },
    mainHeading: {
      fontFamily:
        Platform.OS === "web" ? "Inter_500Medium, Inter" : "Inter_500Medium",
      fontSize: 18,
      fontWeight: "normal",
      lineHeight: 24,
      letterSpacing: 18 * -0.03,
    },
    mainBaseMedium: {
      fontFamily:
        Platform.OS === "web" ? "Inter_500Medium, Inter" : "Inter_500Medium",
      fontSize: 14,
      fontWeight: "normal",
      lineHeight: 18,
      letterSpacing: 14 * -0.02,
    },
    mainBaseSemiBold: {
      fontSize: 14,
      fontWeight: Platform.OS === "web" ? 600 : "normal",
      fontFamily:
        Platform.OS === "web"
          ? "Inter_600SemiBold, Inter"
          : "Inter_600SemiBold",
      lineHeight: 18,
      letterSpacing: 14 * -0.02,
    },
    mainSmallMedium: {
      fontSize: 13,
      fontWeight: "normal",
      fontFamily:
        Platform.OS === "web" ? "Inter_500Medium, Inter" : "Inter_500Medium",
      lineHeight: 16,
      letterSpacing: 13 * -0.02,
    },
    mainSmallSemiBold: {
      fontSize: 13,
      fontWeight: Platform.OS === "web" ? 600 : "normal",
      fontFamily:
        Platform.OS === "web"
          ? "Inter_600SemiBold, Inter"
          : "Inter_600SemiBold",
      lineHeight: 16,
      letterSpacing: 13 * -0.02,
    },
    mainSmall: {
      fontSize: 13,
      fontWeight: "normal",
      fontFamily:
        Platform.OS === "web" ? "Inter_500Medium, Inter" : "Inter_500Medium",
      lineHeight: 16,
      letterSpacing: 13 * -0.02,
    },
    mainTiny: {
      fontSize: 11,
      fontWeight: "normal",
      fontFamily:
        Platform.OS === "web"
          ? "Inter_600SemiBold, Inter"
          : "Inter_600SemiBold",
      lineHeight: 16,
      letterSpacing: 11 * -0.02,
    },
    mainTinyMedium: {
      fontSize: 11,
      fontWeight: "normal",
      fontFamily:
        Platform.OS === "web" ? "Inter_500Medium, Inter" : "Inter_500Medium",
      lineHeight: 16,
      letterSpacing: 11 * -0.02,
    },
    mainMicro: {
      fontSize: 10,
      fontWeight: "normal",
      fontFamily:
        Platform.OS === "web"
          ? "Inter_600SemiBold, Inter"
          : "Inter_600SemiBold",
      lineHeight: 12,
      letterSpacing: 0,
    },
    serifTitle1: {
      fontSize: 40,
      fontWeight: "normal",
      lineHeight: 44,
      letterSpacing: 40 * -0.04,
      fontFamily:
        Platform.OS === "web" ? "Lora_500Medium, Lora" : "Lora_500Medium",
    },
    serifTitle2: {
      fontSize: 32,
      fontWeight: "normal",
      lineHeight: 36,
      letterSpacing: 32 * -0.04,
      fontFamily:
        Platform.OS === "web" ? "Lora_500Medium, Lora" : "Lora_500Medium",
    },
    serifTitle3: {
      fontSize: 24,
      lineHeight: 28,
      fontWeight: "normal",
      letterSpacing: 24 * -0.04,
      fontFamily:
        Platform.OS === "web" ? "Lora_500Medium, Lora" : "Lora_500Medium",
    },
    serifHeading: {
      fontSize: 20,
      lineHeight: 24,
      letterSpacing: 20 * -0.02,
      fontWeight: "normal",
      fontFamily:
        Platform.OS === "web" ? "Lora_500Medium, Lora" : "Lora_500Medium",
    },
    serifBase: {
      fontSize: 16,
      lineHeight: 16,
      fontWeight: "normal",
      letterSpacing: 16 * -0.02,
      fontFamily:
        Platform.OS === "web" ? "Lora_500Medium, Lora" : "Lora_500Medium",
    },
    defaults: {
      color: "text-primary",
      fontFamily:
        Platform.OS === "web" ? "Inter_500Medium, Inter" : "Inter_500Medium",
      fontSize: 14,
      lineHeight: 18,
      letterSpacing: 14 * -0.02,
    },
  },
});

export type Theme = typeof theme;
